import { EMPTY_AVATAR_GREY_SRC } from 'constants/user';
import Clock from 'svg/Clock';
import Crown from 'svg/Crown';
import BadgeRating from 'components/BadgeRating';
import { Button } from 'components/Button';
import type { FindGamePlayer } from 'components/Find/types';
import VerificationBadge from 'components/VerificationBadge';
import classNames from 'styles/utils/classNames';

interface Props {
  /** `null` renders an unfilled slot. */
  player: FindGamePlayer | null;
  /**
   * Whether an empty seat may be labelled "open". False for viewers who can't read
   * accepted requests — the seat may well be taken, and we don't know.
   */
  canLabelAsOpen?: boolean;
  /** Shrinks the seat to fit the mobile feed's 120px roster tile. */
  compact?: boolean;
  /** The details modal's seat from lg up: a 64px avatar, or 32px when `compact`. */
  isLargeAtDesktop?: boolean;
  /** Host-only: puts an "Invite" pill under an empty seat. */
  onInvite?: () => void;
  className?: string;
}

export default function PlayerSlot({
  player,
  canLabelAsOpen = true,
  compact = false,
  isLargeAtDesktop = false,
  onInvite,
  className,
}: Props) {
  const isBigSeatAtDesktop = isLargeAtDesktop && !compact;
  const canInvite = !compact && !player && canLabelAsOpen && !!onInvite;

  return (
    <div
      className={classNames(
        'flex min-w-0 flex-1 flex-col items-center gap-ds-xs',
        canInvite ? 'justify-start' : 'justify-center',
        compact ? 'pt-0' : 'pt-ds-xs',
        isBigSeatAtDesktop && 'lg:pt-ds-sm',
        className,
      )}
    >
      <div
        className={classNames(
          'relative shrink-0',
          compact ? 'h-7 w-7' : 'h-12 w-12',
          isLargeAtDesktop && (compact ? 'lg:h-8 lg:w-8' : 'lg:h-16 lg:w-16'),
        )}
      >
        <img
          src={player?.imageUrl ?? EMPTY_AVATAR_GREY_SRC}
          alt={player?.name ?? (canLabelAsOpen ? 'Open slot' : 'Player slot')}
          className={classNames(
            'h-full w-full rounded-full border-2 border-color-bg-lightmode-primary object-cover dark:border-color-bg-darkmode-primary',
            isBigSeatAtDesktop && 'lg:border-[3px]',
          )}
        />

        <VerificationBadge
          level={player?.verificationLevel}
          className={classNames(
            compact ? 'size-3' : 'size-4',
            isBigSeatAtDesktop && 'lg:size-[1.3125rem]',
          )}
        />

        {player?.isHost && (
          <Crown
            className={classNames(
              'absolute -left-1.5 -top-2.5 h-4 w-4 -rotate-[37deg] text-brand-yellow-500',
              isBigSeatAtDesktop && 'lg:-left-2 lg:-top-3.5 lg:h-[1.3125rem] lg:w-[1.3125rem]',
            )}
          />
        )}
      </div>

      <div className="flex flex-col items-center gap-ds-xs">
        {(player || canLabelAsOpen) && (
          <span
            className={classNames(
              'max-w-full truncate text-center',
              compact
                ? 'typography-product-button-label-xs'
                : 'typography-product-button-label-small',
              isBigSeatAtDesktop && 'lg:typography-product-button-label-medium',
              player
                ? 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary'
                : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
            )}
          >
            {player?.name ?? 'open'}
          </span>
        )}

        {!compact && player?.isPending && (
          <span className="typography-product-button-label-xs flex h-5 items-center gap-ds-xs rounded-xl bg-brand-yellow-100 px-1.5 py-0.5 text-brand-yellow-800">
            <Clock className="size-3 shrink-0" />
            Pending
          </span>
        )}

        {!compact && !player?.isPending && player?.isInvited && (
          <span className="typography-product-button-label-xs flex h-5 items-center justify-center rounded-xl bg-brand-blue-100 px-1.5 text-brand-blue-800">
            Invited
          </span>
        )}

        {!compact && player && !player.isInvited && !player.isPending && player.skillLevel && (
          <BadgeRating
            level={player.skillLevel}
            label={player.ratingLabel}
            size="xs"
            sizeDesktop={isLargeAtDesktop ? 'sm' : undefined}
          />
        )}
      </div>

      {canInvite && (
        <Button variant="primary" size="xs" isInline onClick={onInvite}>
          Invite
        </Button>
      )}
    </div>
  );
}
